import isNaN from "lodash/isNaN"
import filter from "lodash/filter"
import get from "lodash/get"
import isNumber from "lodash/isNumber"
/**
 * Constructs a response object with updated product details.
 * @param {Object} productsObject - The original products object.
 * @param {string} productsObject.request_tracking_id - The request tracking ID.
 * @param {Array} productsObject.customers_results - The results of the customers.
 * @param {boolean} productsObject.is_existing_customer - Flag indicating if the customer is existing.
 * @param {boolean} productsObject.is_franchise_available - Flag indicating if the customer is existing.
 * @param {boolean} productsObject.is_eligible_customer - Flag indicating if the customer is eligible.
 * @param {Array} productsObject.eligibility_results - The results of the eligibility check.
 * @param {string} productsObject.pricing_engine_request_id - The pricing engine request ID for long hauls.
 * @param {Array} productsObject.zones_results - The results of the zones.
 * @param {Array} productsObject.products_results - The results of the products.
 * @param {string} productsObject.request_quote_form - The request quote form.
 * @returns {Object} An object containing updated product details.
 */
export const getProductsUpdatedResponse = (productsObject) => {
  const responseObject = {
    request_tracking_id: productsObject.request_tracking_id,
    customers_results: productsObject.customers_results,
    is_existing_customer: productsObject.is_existing_customer,
    is_pricing_available: productsObject.is_pricing_available,
    is_service_available: productsObject.is_service_available,
    is_eligible_customer: productsObject.is_eligible_customer,
    is_franchise_available: productsObject.is_franchise_available,
    eligibility_results: productsObject.eligibility_results,
    pricing_engine_request_id_long_hauls:
      productsObject.pricing_engine_request_id,
    zones_results: productsObject.zones_results,
    zones: getZones(productsObject),
    products_results: productsObject.products_results,
    products: getProducts(productsObject),
    request_quote_form: get(productsObject, `request_quote_form`, ``),
  }
  return responseObject
}

/**
 * Constructs an array of zones from the given products object.
 * @param {Object} productsObject - The original products object.
 * @param {Object[]} productsObject.zones - The array of zones in the products object.
 * @param {string} productsObject.zones.id - The ID of the zone.
 * @param {string} productsObject.zones.line_of_business - The line of business of the zone.
 * @param {string} productsObject.zones.contract_type_code - The contract type code of the zone.
 * @param {string} productsObject.zones.type_indicator_code - The type indicator code of the zone.
 * @param {string} productsObject.zones.business_unit - The business unit of the zone.
 * @param {string} productsObject.zones.site_id - The site ID of the zone.
 * @param {string} productsObject.zones.site_name - The site name of the zone.
 * @param {string} productsObject.zones.waste_stream - The waste stream of the zone.
 * @param {string} productsObject.zones.waste_stream_code - The waste stream code of the zone.
 * @param {string} productsObject.zones.truck_type_code - The truck type code of the zone.
 * @param {string} productsObject.zones.hyper_link - The hyperlink of the zone.
 * @returns {Array} An array of zones with updated details.
 */
export const getZones = (productsObject) => {
  let zones = []
  if (
    productsObject &&
    productsObject.zones &&
    productsObject.zones.length > 0
  ) {
    zones = productsObject.zones.map((zone) => ({
      pricing_service_type: zone.PRICE_SERVC_TYPE,
      id: zone.id,
      line_of_business: zone.line_of_business,
      contract_type_code: zone.contract_type_code,
      type_indicator_code: zone.type_indicator_code,
      business_unit: zone.business_unit,
      site_id: zone.site_id,
      site_name: zone.site_name,
      waste_stream: zone.waste_stream,
      waste_stream_code: zone.waste_stream_code,
      truck_type_code: zone.truck_type_code,
      hyper_link: zone.hyper_link,
      market_area_name: zone.market_area_name,
    }))
  }
  return zones
}

/**
 * Constructs an array of products from the given products object.
 * @param {Object} productsObject - The original products object.
 * @param {Object[]} productsObject.products - The array of products in the products object.
 * @param {string} productsObject.products.id - The ID of the product.
 * @param {string} productsObject.products.reference_id - The reference ID of the product.
 * @param {string} productsObject.products.name - The name of the product.
 * @param {string} productsObject.products.type - The type of the product.
 * @param {string} productsObject.products.container_size - The container size of the product.
 * @param {string} productsObject.products.maximum_weight - The maximum weight of the product.
 * @param {string} productsObject.products.category_id - The category ID of the product.
 * @param {string} productsObject.products.uom - The unit of measure of the product.
 * @param {string} productsObject.products.equipment_code - The equipment code of the product.
 * @param {string} productsObject.products.currency - The currency of the product.
 * @param {string} productsObject.products.delivery_lead_time - The delivery lead time of the product.
 * @param {string} productsObject.products.free_days_included - The free days included with the product.
 * @param {string} productsObject.products.facility_id - The facility ID of the product.
 * @param {string} productsObject.products.price_per_additional_day - The price per additional day of the product.
 * @param {string} productsObject.products.pricing_reference_by_zone_id1 - The pricing reference by zone ID 1 of the product.
 * @param {string} productsObject.products.pricing_reference_by_zone_id2 - The pricing reference by zone ID 2 of the product.
 * @param {string} productsObject.products.hyperLink - The hyperlink of the product.
 * @param {string} productsObject.products.business_unit_number - The business unit number of the product.
 * @returns {Array} An array of products with updated details.
 */
export const getProducts = (productsObject) => {
  let products = []
  if (
    productsObject &&
    productsObject.products &&
    productsObject.products.length > 0
  ) {
    products = productsObject.products.map((product) => ({
      id: product.id,
      reference_id: product.reference_id,
      name: product.name,
      type: product.type,
      container_size: product.container_size,
      maximum_weight: product.maximum_weight,
      category_id: product.category_id,
      uom: product.uom,
      isFranchiseZonesAvailable: product?.isFranchiseZonesAvailable,
      haulPrice: product?.attributes?.haulPrice,
      haulDeliveryPrice: product?.attributes?.haulDeliveryPrice,
      tonsOverage: product?.attributes?.tonsOverage,
      isCCprice: product?.isCCprice,
      energyFee: product?.energyFee,
      hasEnergyCharge: product?.hasEnergyCharge,
      fuelFee: product?.fuelFee,
      envFee: product?.envFee,
      rcrFee: product?.rcrFee,
      equipment_code: product.equipment_code,
      currency: product.currency,
      delivery_lead_time: product.delivery_lead_time,
      free_days_included: product.free_days_included,
      facility_id: product.facility_id,
      price_per_additional_day: product.price_per_additional_day,
      pricing_reference_by_zone_id1: product.pricing_reference_by_zone_id1,
      pricing_reference_by_zone_id2: product.pricing_reference_by_zone_id2,
      hyperLink: product.hyperLink,
      business_unit_number: product.business_unit_number,
      attributes: getAttributes(
        productsObject,
        productsObject.products,
        product.id,
      ),
      ancillary_services: [],
    }))
  }
  return products
}

/**
 * Constructs an array of attribute objects from the given products object.
 * @param {Object} productsObject - The original products object.
 * @param {Object[]} products - The array of products.
 * @param {string} productId - The ID of the product.
 * @returns {Array} An array of attribute objects with updated details.
 */
export const getAttributes = (productsObject, products, productId) => {
  const longTermObject = getAttributesObject(
    productsObject,
    products,
    productId,
    "long",
  )
  return [longTermObject]
}

/**
 * Constructs an attribute object from the given products object.
 * @param {Object} productsObject - The original products object.
 * @param {string} productsObject.pricing_engine_request_id - The pricing engine request ID of the products object.
 * @param {string} productsObject.service_type - The service type of the products object.
 * @param {Object[]} products - The array of products.
 * @param {string} productId - The ID of the product.
 * @param {string} term - The term of the product.
 * @returns {Object} An attribute object with updated details.
 */
export const getAttributesObject = (
  productsObject,
  products,
  productId,
  term,
) => {
  const attributeObject = {
    pricing_engine_request_id: productsObject.pricing_engine_request_id,
    service_type: productsObject.service_type,
    materials: [],
  }
  if (products && products.length > 0) {
    const uniqueProductsList = filter(products, { id: productId })
    if (uniqueProductsList && uniqueProductsList.length > 0) {
      uniqueProductsList.forEach((product) => {
        const delivery_fee_fuel =
          product?.attributes?.delivery_fee *
            product?.attributes?.pricing?.effective_fuel_percentage +
          product?.attributes?.delivery_fee
        const delivery_fee_environment =
          delivery_fee_fuel *
            product?.attributes?.pricing?.effective_environmental_percentage +
          delivery_fee_fuel

        const delivery_fee_regulatory =
          delivery_fee_environment *
            product?.attributes?.pricing?.effective_regulatory_percentage +
          delivery_fee_environment

        const elivery_fee_environment_copy =
          isNaN(delivery_fee_regulatory) || !delivery_fee_regulatory
            ? product?.attributes?.delivery_fee
            : delivery_fee_regulatory

        const delivery_fee_energy = product?.attributes?.pricing
          ?.effective_energy_percentage
          ? elivery_fee_environment_copy *
              product?.attributes?.pricing?.effective_energy_percentage +
            elivery_fee_environment_copy
          : elivery_fee_environment_copy

        const material = {
          name: product.waste_type,
          id: product.attributes.id,
          inactivity_charge_type: product.inactivity_charge_type,
          inactivity_charge: product.inactivity_charge,
          minimum_monthly_hauls: product.minimum_monthly_hauls,
          long_term_days: product.long_term_days,
          minimum_weight_included: product.attributes.minimum_weight_included,
          term_type: term,
          agreement_type: "Open Market",
          delivery_fee: delivery_fee_energy,
          fuel_percentage:
            product?.attributes?.pricing?.effective_fuel_percentage,
          environment_percentage:
            product?.attributes?.pricing?.effective_environmental_percentage,
          regulatory_percentage:
            product?.attributes?.pricing?.effective_regulatory_percentage,
          pricing: {
            id: product?.attributes?.pricing?.id,
            price_level: product.price_level,
            base_amount:
              product.attributes.pricing[
                `base_${(product.price_level || "")
                  .toLowerCase()
                  .replace(" ", "_")}_haul_rate`
              ],
            haul_amount:
              product.attributes.pricing[
                `allin_${(product.price_level || "")
                  .toLowerCase()
                  .replace(" ", "_")}_haul_rate`
              ],
            effective_energy_percentage:
              product?.attributes?.pricing?.effective_energy_percentage,
            per_additional_uom:
              product.attributes.pricing[
                `allin_${(product.price_level || "")
                  .toLowerCase()
                  .replace(" ", "_")}_disposal_rate`
              ],
            allin_absolute_floor_disposal_rate:
              product?.attributes?.pricing?.allin_absolute_floor_disposal_rate,
            allin_absolute_floor_haul_rate:
              product?.attributes?.pricing?.allin_absolute_floor_haul_rate,
            allin_expert_disposal_rate:
              product?.attributes?.pricing?.allin_expert_disposal_rate,
            allin_expert_haul_rate:
              product?.attributes?.pricing?.allin_expert_haul_rate,
            allin_floor_disposal_rate:
              product?.attributes?.pricing?.allin_floor_disposal_rate,
            allin_floor_haul_rate:
              product?.attributes?.pricing?.allin_floor_haul_rate,
            allin_target_disposal_rate:
              product?.attributes?.pricing?.allin_target_disposal_rate,
            allin_target_haul_rate:
              product?.attributes?.pricing?.allin_target_haul_rate,
            base_absolute_floor_disposal_rate:
              product?.attributes?.pricing?.base_absolute_floor_disposal_rate,
            base_absolute_floor_haul_rate:
              product?.attributes?.pricing?.base_absolute_floor_haul_rate,
            base_expert_haul_rate:
              product?.attributes?.pricing?.base_expert_haul_rate,
            base_floor_disposal_rate:
              product?.attributes?.pricing?.base_floor_disposal_rate,
            base_expert_disposal_rate:
              product?.attributes?.pricing?.base_expert_disposal_rate,
            base_floor_haul_rate:
              product?.attributes?.pricing?.base_floor_haul_rate,
            base_target_disposal_rate:
              product?.attributes?.pricing?.base_target_disposal_rate,
            base_target_haul_rate:
              product?.attributes?.pricing?.base_target_haul_rate,
          },
          overages: [],
        }
        const disposalRateKey = `allin_${(product.price_level || "")
          .toLowerCase()
          .replace(" ", "_")}_disposal_rate`

        const disposalRate = product.attributes.pricing?.[disposalRateKey]
        const minWeight = product.attributes?.minimum_weight_included
        const getAdditionalTonPrice = (basePrice) => {
          if (isNumber(basePrice) && basePrice > 0) {
            if (get(product, `hasEnergyCharge`)) {
              const energySurcharge = get(product, `energyFee`, 0)

              return (basePrice * (1 + energySurcharge / 100))?.toFixed(2)
            }
            const fuelFee = get(product, `fuelFee`, 0)
            const envFee = get(product, `envFee`, 0)
            const rcrFee = get(product, `rcrFee`, 0)
            const calculatedFuelFee = (basePrice * (1 + fuelFee / 100)).toFixed(
              2,
            )
            const calculatedEnvFee = (
              calculatedFuelFee *
              (1 + envFee / 100)
            ).toFixed(2)
            const calculatedRcrFee = (
              calculatedEnvFee *
              (1 + rcrFee / 100)
            ).toFixed(2)

            return calculatedRcrFee
          }
          return basePrice
        }

        for (let i = minWeight + 1; i <= 10; i++) {
          material.overages.push({
            weight: i.toString(),
            amount: (
              (product?.isCCprice
                ? getAdditionalTonPrice(product?.attributes?.tonsOverage)
                : disposalRate) *
              (i - minWeight)
            )
              .toFixed(2)
              .toString(),
          })
        }
        attributeObject.materials.push(material)
      })
    }
  }
  return attributeObject
}

/**
 * Checks if the products have long-term service.
 * @param {Object} finalResponse - The final response object containing products.
 * @returns {boolean} True if there is a long-term service, otherwise false.
 */
export const hasLongTermService = (finalResponse) => {
  const products = get(finalResponse, "products", [])
  let hasService = false

  products.forEach((item) => {
    const longTerm = item.attributes?.find((s) => s.service_type === "longTerm")
    if (longTerm?.materials?.length > 0) {
      hasService = true
    }
  })

  return hasService
}

/**
 * Finds a zone based on the provided criteria.
 * @param {Array} zones - The array of zones to search through.
 * @param {number} typeIndicatorCode - The type indicator code to match.
 * @param {number} contractTypeCode - The contract type code to match.
 * @param {string} lineOfBusiness - The line of business to match.
 * @returns {Object|undefined} The found zone object or undefined if no match is found.
 */
export const findZone = (
  zones,
  typeIndicatorCode,
  contractTypeCode,
  lineOfBusiness,
) =>
  zones?.find(
    (s) =>
      s?.type_indicator_code === typeIndicatorCode &&
      s?.contract_type_code === contractTypeCode &&
      s?.line_of_business === lineOfBusiness,
  )
